import { useMemo } from 'react'
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { motion } from 'framer-motion'
import { Virtuoso } from 'react-virtuoso'
import { WorkOrderCard } from '../components/WorkOrderCard'
import { getStatusMeta, type DummyWorkOrder, type WoStatus } from '../dummy'
import { useIsTaskModule } from '../moduleContext'

const DEFAULT_COLUMNS: WoStatus[] = ['open', 'in_progress', 'closed', 'resolved']

export function KanbanView({ workOrders, hiddenScheduledCount = 0 }: {
  workOrders: DummyWorkOrder[]
  /** Real (non-preview) scheduled rows the default fetch excludes
   *  entirely (see work-orders.service.js line 236) — see
   *  WorkOrdersPage.tsx's scheduledCountQ. Without this, the
   *  Scheduled column's "real" count is always 0 here (every actual
   *  scheduled row is excluded before it ever reaches this
   *  component), which reads as "no scheduled items exist" even when
   *  several do — they're just hidden behind the toggle. */
  hiddenScheduledCount?: number
}) {
  const isTask = useIsTaskModule()
  // Show a Scheduled column only when there's at least one scheduled WO
  // in the loaded set (the user opted in via the "Showing scheduled"
  // toggle). Without this, the page's data filter strips them silently.
  const hasScheduled = useMemo(
    () => workOrders.some((w) => w.status === 'scheduled'),
    [workOrders],
  )
  const COLUMNS: WoStatus[] = hasScheduled
    ? ['scheduled', ...DEFAULT_COLUMNS]
    : DEFAULT_COLUMNS

  // Bucket once per data change rather than once per render. Without
  // this, filter chains (search/priority/etc.) re-bucket O(n × cols)
  // on every keystroke — at 3K WOs that's a 12K-row scan per char.
  const grouped = useMemo(
    () => COLUMNS.map((status) => ({
      status,
      items: workOrders.filter((w) => w.status === status),
    })),
    // COLUMNS depends on hasScheduled which depends on workOrders;
    // listing workOrders alone is enough to invalidate.
    [workOrders, hasScheduled],
  )

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowX: 'auto' }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(320px, 1fr))`,
        gap: 2,
        minWidth: COLUMNS.length * 320 + (COLUMNS.length - 1) * 16,
        height: '100%',
      }}
    >
      {grouped.map(({ status, items }, columnIndex) => {
        // Column header uses the module-aware label so the Tasks
        // kanban shows "Completed" while Work Orders keeps "Resolved".
        const meta = getStatusMeta(status, isTask)
        // The Scheduled column mixes real rows with synthetic look-
        // ahead previews of a recurring template's future occurrences
        // (work-orders.service.js's "Upcoming preview rows" — read-only,
        // not persisted, exist only to show what's coming). A single
        // combined count reads as "N real items" when it's actually
        // "M real + preview instances of the same handful of templates",
        // which doesn't match what you get if you click through.
        const previewCount = items.filter((w) => w._raw.is_preview).length
        // The Scheduled column's own `items` never contain real rows
        // when previews are present — the default fetch excludes
        // status='scheduled' outright (previews stand in for them).
        // Fall back to the separately-fetched real count instead of
        // the structurally-always-0 `items.length - previewCount`.
        const realCount = status === 'scheduled' && previewCount > 0
          ? hiddenScheduledCount
          : items.length - previewCount
        const countLabel = previewCount > 0 ? `${realCount} · +${previewCount} upcoming` : items.length
        return (
          <motion.div
            key={status}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: columnIndex * 0.05, duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            style={{ display: 'flex', minHeight: 0, minWidth: 0, overflow: 'hidden' }}
          >
          <Box
            sx={{
              flex: 1,
              bgcolor: (t) =>
                t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {/* Column header */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  width: 10, height: 10, borderRadius: 99,
                  bgcolor: meta.color,
                  boxShadow: `0 0 0 3px ${meta.color}22`,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12.5, fontWeight: 700,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                {meta.label}
              </Typography>
              <Chip
                label={countLabel}
                size="small"
                sx={{
                  height: 19,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  '& .MuiChip-label': { px: 0.85 },
                }}
              />
              <Box sx={{ flex: 1 }} />
              <Tooltip title={`Add to ${meta.label}`} arrow>
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Cards — virtualized via react-virtuoso so only the
                visible cards (+ a small overscan buffer) actually
                mount. At 3K WOs the previous render-everything
                approach reconciled 3K WorkOrderCards on every parent
                re-render, freezing the main thread. Virtuoso handles
                variable card heights automatically — no measurement
                config needed. */}
            {items.length === 0 ? (
              <Box sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    py: 4, textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Nothing here yet.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Virtuoso
                  style={{ height: '100%' }}
                  data={items}
                  // 200px overscan keeps adjacent cards mounted so
                  // fast scrolls don't flash empty space before
                  // virtuoso catches up.
                  increaseViewportBy={200}
                  itemContent={(_idx, wo) => (
                    <Box sx={{ px: 1.5, pt: 1.5 }}>
                      <WorkOrderCard wo={wo} />
                    </Box>
                  )}
                />
              </Box>
            )}
          </Box>
          </motion.div>
        )
      })}
    </Box>
    </Box>
  )
}
